import { supabase } from '../supabaseClient';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

export interface TeamLeaderAcademicCalendarSettingsPayload {
  academic_year: number;
  year_starts_on: string;
  year_ends_on: string;
  summer_break_starts_on: string;
  summer_break_ends_on: string;
  winter_break_starts_on: string | null;
  winter_break_ends_on: string | null;
}

export interface TeamLeaderAcademicCalendarDayPayload {
  day: string;
  day_kind: 'class' | 'holiday' | 'closure' | 'vacation';
  note: string | null;
}

export interface TeamLeaderAcademicCalendarSectionPreviewRow {
  section_id: string;
  grade_labels: string[];
  settings: TeamLeaderAcademicCalendarSettingsPayload | null;
  days: TeamLeaderAcademicCalendarDayPayload[];
  holiday_count: number;
  weekend_class_count: number;
}

export interface TeamLeaderAcademicCalendarPreviewPayload {
  leader_workspace_id: string;
  sections: TeamLeaderAcademicCalendarSectionPreviewRow[];
}

export interface TeamLeaderAcademicCalendarImportPayload {
  settings: TeamLeaderAcademicCalendarSettingsPayload;
  days: TeamLeaderAcademicCalendarDayPayload[];
}

function parseSettings(raw: unknown): TeamLeaderAcademicCalendarSettingsPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  let academicYear = Number(o.academic_year ?? 0);
  const yearStartsOn = String(o.year_starts_on ?? '');
  if (!academicYear && yearStartsOn.length >= 4) {
    academicYear = Number(yearStartsOn.slice(0, 4));
  }
  if (!academicYear) return null;
  return {
    academic_year: academicYear,
    year_starts_on: yearStartsOn,
    year_ends_on: String(o.year_ends_on ?? ''),
    summer_break_starts_on: String(o.summer_break_starts_on ?? ''),
    summer_break_ends_on: String(o.summer_break_ends_on ?? ''),
    winter_break_starts_on: o.winter_break_starts_on ? String(o.winter_break_starts_on) : null,
    winter_break_ends_on: o.winter_break_ends_on ? String(o.winter_break_ends_on) : null,
  };
}

function parseDays(raw: unknown): TeamLeaderAcademicCalendarDayPayload[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const day = String(o.day ?? '');
      const dayKind = String(o.day_kind ?? 'holiday');
      if (!day) return null;
      if (dayKind !== 'class' && dayKind !== 'holiday' && dayKind !== 'closure' && dayKind !== 'vacation') return null;
      return {
        day,
        day_kind: dayKind,
        note: o.note == null ? null : String(o.note),
      };
    })
    .filter((row): row is TeamLeaderAcademicCalendarDayPayload => row != null);
}

export async function getTeamLeaderAcademicCalendarSectionsPreview(
  teamId: string
): Promise<TeamLeaderAcademicCalendarPreviewPayload> {
  const { data, error } = await supabase.rpc('get_team_leader_academic_calendar_sections_preview', {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') return { leader_workspace_id: '', sections: [] };
  const sectionsRaw = raw.sections;
  const sections: TeamLeaderAcademicCalendarSectionPreviewRow[] = [];
  if (Array.isArray(sectionsRaw)) {
    for (const row of sectionsRaw) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      const labelsRaw = o.grade_labels;
      sections.push({
        section_id: String(o.section_id ?? ''),
        grade_labels: Array.isArray(labelsRaw) ? labelsRaw.map((x) => String(x)) : [],
        settings: parseSettings(o.settings),
        days: parseDays(o.days),
        holiday_count: Number(o.holiday_count ?? 0) || 0,
        weekend_class_count: Number(o.weekend_class_count ?? 0) || 0,
      });
    }
  }
  return { leader_workspace_id: String(raw.leader_workspace_id ?? ''), sections };
}

export async function importTeamLeaderAcademicCalendarSection(
  teamId: string,
  targetWorkspaceId: string,
  sourceSectionId: string,
  targetSectionId: string
): Promise<TeamLeaderAcademicCalendarImportPayload> {
  const { data, error } = await supabase.rpc('import_team_academic_calendar_section', {
    p_team_id: teamId,
    p_target_workspace_id: targetWorkspaceId,
    p_source_section_id: sourceSectionId,
    p_target_section_id: targetSectionId,
  });
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(targetWorkspaceId);
  const raw = data as Record<string, unknown> | null;
  const settings = parseSettings(raw?.settings);
  if (!settings) throw new Error('팀장 학사일정 설정을 읽을 수 없습니다.');
  return { settings, days: parseDays(raw?.days) };
}
